package com.arena_web.data.repository

import com.arena_web.data.api.EventApi
import com.arena_web.data.dto.*
import com.arena_web.domain.repositories.EventRepository
import io.ktor.http.*

class EventRepositoryImpl(private val eventApi: EventApi) : EventRepository {

    override suspend fun getEvents(params: Map<String, String>): Result<EventsPageDto> =
        apiCall { eventApi.getEvents(params) }

    override suspend fun getUpcomingCities(sportId: String?): Result<List<String>> =
        apiCall { eventApi.getUpcomingCities(sportId) }

    override suspend fun getEvent(eventId: String): Result<EventDetailDto> =
        apiCall { eventApi.getEvent(eventId) }

    override suspend fun createEvent(request: CreateEventRequest): Result<EventDetailDto> =
        apiCall { eventApi.createEvent(request) }

    override suspend fun updateEvent(eventId: String, request: UpdateEventRequest): Result<EventDetailDto> =
        apiCall { eventApi.updateEvent(eventId, request) }

    override suspend fun deleteEvent(eventId: String): Result<Unit> =
        apiCallUnit { eventApi.deleteEvent(eventId) }

    override suspend fun updateEventStatus(eventId: String, request: UpdateEventStatusRequest): Result<EventDetailDto> =
        apiCall { eventApi.updateEventStatus(eventId, request) }

    override suspend fun joinEvent(eventId: String): Result<EventParticipantDto> =
        apiCall { eventApi.joinEvent(eventId) }

    override suspend fun leaveEvent(eventId: String): Result<Unit> =
        apiCallStatus { eventApi.leaveEvent(eventId) }

    override suspend fun confirmParticipant(eventId: String, userId: String): Result<EventParticipantDto> =
        apiCall { eventApi.confirmParticipant(eventId, userId) }

    override suspend fun rejectParticipant(eventId: String, userId: String): Result<Unit> =
        apiCallStatus { eventApi.rejectParticipant(eventId, userId) }

    override suspend fun assignTeam(eventId: String, request: AssignTeamRequest): Result<EventParticipantDto> =
        apiCall { eventApi.assignTeam(eventId, request) }

    override suspend fun assignUserToTeam(eventId: String, userId: String, request: AssignTeamRequest): Result<EventParticipantDto> =
        apiCall { eventApi.assignUserToTeam(eventId, userId, request) }

    override suspend fun leaveTeam(eventId: String): Result<EventParticipantDto> =
        apiCall { eventApi.leaveTeam(eventId) }

    override suspend fun removeUserFromTeam(eventId: String, userId: String): Result<EventParticipantDto> =
        apiCall { eventApi.removeUserFromTeam(eventId, userId) }

    override suspend fun setParticipantAdmin(eventId: String, userId: String, isAdmin: Boolean): Result<EventParticipantDto> =
        apiCall { eventApi.setParticipantAdmin(eventId, userId, SetAdminRequest(isAdmin)) }

    override suspend fun addTeam(eventId: String, request: AddEventTeamRequest): Result<EventTeamDto> =
        apiCall { eventApi.addTeam(eventId, request) }

    override suspend fun deleteTeam(eventId: String, teamId: String): Result<Unit> =
        apiCallStatus { eventApi.deleteTeam(eventId, teamId) }

    override suspend fun setTeamScore(eventId: String, teamId: String, request: SetTeamScoreRequest): Result<Unit> =
        apiCallUnit { eventApi.setTeamScore(eventId, teamId, request) }

    override suspend fun createTeamInvitation(eventId: String, request: CreateTeamInvitationRequest): Result<TeamInvitationDto> =
        apiCall { eventApi.createTeamInvitation(eventId, request) }

    override suspend fun getTeamInvitations(eventId: String): Result<List<TeamInvitationDto>> =
        apiCall { eventApi.getTeamInvitations(eventId) }

    override suspend fun respondTeamInvitation(eventId: String, invitationId: String, request: RespondTeamInvitationRequest): Result<Unit> =
        apiCallUnit { eventApi.respondTeamInvitation(eventId, invitationId, request) }

    override suspend fun getStats(eventId: String): Result<EventPlayerStatsResultDto> =
        apiCall { eventApi.getStats(eventId) }

    override suspend fun setPlayerStats(eventId: String, userId: String, request: SetPlayerStatsRequest): Result<Unit> =
        apiCallUnit { eventApi.setPlayerStats(eventId, userId, request) }

    override suspend fun swapLineup(eventId: String, request: SwapLineupRequest): Result<Unit> =
        apiCallUnit { eventApi.swapLineup(eventId, request) }

    override suspend fun swapEventTeams(eventId: String, request: SwapEventTeamsRequest): Result<Unit> =
        apiCallUnit { eventApi.swapEventTeams(eventId, request) }

    override suspend fun getRatings(eventId: String): Result<List<EventPlayerRatingDto>> =
        apiCall { eventApi.getRatings(eventId) }

    override suspend fun ratePlayer(eventId: String, userId: String, request: RatePlayerRequest): Result<EventPlayerRatingDto> =
        apiCall { eventApi.ratePlayer(eventId, userId, request) }

    override suspend fun toggleExtendedStats(eventId: String, request: ToggleExtendedStatsRequest): Result<EventDetailDto> =
        apiCall { eventApi.toggleExtendedStats(eventId, request) }

    override suspend fun getTeamStats(eventId: String): Result<EventTeamStatsResultDto> =
        apiCall { eventApi.getTeamStats(eventId) }

    override suspend fun setTeamStats(eventId: String, teamId: String, request: SetTeamStatsRequest): Result<Unit> =
        apiCallUnit { eventApi.setTeamStats(eventId, teamId, request) }

    override suspend fun getTimer(eventId: String): Result<EventTimerDto> =
        apiCall { eventApi.getTimer(eventId) }

    override suspend fun updateTimer(eventId: String, request: EventTimerActionRequest): Result<EventTimerDto> =
        apiCall { eventApi.updateTimer(eventId, request) }

    override suspend fun getSubstitutions(eventId: String): Result<List<EventSubstitutionDto>> =
        apiCall { eventApi.getSubstitutions(eventId) }

    override suspend fun makeSubstitution(eventId: String, request: MakeSubstitutionRequest): Result<EventSubstitutionDto> =
        apiCall { eventApi.makeSubstitution(eventId, request) }

    override suspend fun getMedia(eventId: String): Result<List<EventMediaDto>> =
        apiCall { eventApi.getMedia(eventId) }

    override suspend fun deleteMedia(eventId: String, mediaId: String): Result<Unit> =
        apiCallUnit { eventApi.deleteMedia(eventId, mediaId) }

    override suspend fun uploadMedia(
        eventId: String,
        fileBytes: ByteArray,
        fileName: String,
        contentType: String,
    ): Result<EventMediaDto> =
        apiCall { eventApi.uploadMedia(eventId, fileBytes, fileName, contentType) }

    override suspend fun uploadEventImage(fileBytes: ByteArray, fileName: String, contentType: String): Result<String> {
        return try {
            val response = eventApi.uploadEventImage(fileBytes, fileName, contentType)
            if (response.data != null) {
                Result.success(response.data.imageUrl)
            } else {
                val errorMsg = response.errors?.joinToString() ?: "Upload failed"
                Result.failure(Exception(errorMsg))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }
}
